import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();

  // Check if already logged in and redirect
  useEffect(() => {
    if (adminAuthService.isAuthenticated() && adminAuthService.isAdmin()) {
      const currentUser = adminAuthService.getCurrentUser();
      if (currentUser && currentUser.role === 'packaging_admin') {
        navigate('/admin/packaging', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trim whitespace and validate
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedUsername || !trimmedPassword) {
      setError('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Use Admin JWT authentication service
      const result = await adminAuthService.login(trimmedUsername, trimmedPassword);
      
      if (result.success && result.admin) {
        setSuccess('تم تسجيل دخول المشرف بنجاح! جاري التوجيه...');
        
        setTimeout(() => {
          // Redirect based on role
          if (result.admin.role === 'packaging_admin') {
            navigate('/admin/packaging');
          } else {
            navigate('/admin/dashboard');
          }
        }, 1000);
      } else {
        setError('فشل في تسجيل الدخول. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      if (error.message.includes('Invalid admin credentials') || error.message.includes('Authentication failed')) {
        setError('اسم المستخدم أو كلمة المرور غير صحيحة أو ليست لديك صلاحية مشرف');
      } else if (error.message.includes('Network')) {
        setError('خطأ في الاتصال بالخادم. يرجى المحاولة لاحقاً');
      } else {
        setError('حدث خطأ في تسجيل الدخول. يرجى المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleBackToCustomer = () => {
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', pt: { xs: 10, md: 12 } }}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <AdminIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2 }}>
            دخول لوحة الإدارة
          </Typography>
          <Typography variant="body1" color="text.secondary">
            أدخل بيانات المشرف للدخول إلى لوحة التحكم
          </Typography>
        </Box>

        {/* Admin Login Form */}
        <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="اسم المستخدم"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AdminIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#d32f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#d32f2f',
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="كلمة المرور"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#d32f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#d32f2f',
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                  '&:disabled': {
                    backgroundColor: '#ffcdd2',
                  },
                }}
              >
                {loading ? 'جاري تسجيل الدخول...' : 'دخول لوحة الإدارة'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }}>
            {success}
          </Alert>
        )}

        {/* Back to Customer Login */}
        <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderRadius: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              هل أنت عميل؟
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={handleBackToCustomer}
              sx={{
                color: '#3b5dac',
                borderColor: '#3b5dac',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#3b5dac',
                  color: 'white',
                },
              }}
            >
              دخول العملاء
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminLogin;
